import { Injectable } from '@angular/core'
import {
    UpClient,
    UpClientBuilder,
    UpClientFile,
    UpClientInitError,
} from '../model/up-client-type-mappings'
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    of,
    switchMap,
    tap,
    throwError,
    Subject,
    startWith,
    combineLatest,
} from 'rxjs'
import { ToasterService } from './toaster.service'
import { UploadApiService } from './upload-api.service'
import { HttpErrorResponse } from '@angular/common/http'
import { UploadFileComponentModel } from '../../case-management/case-management-dialog/upload/upload-file/upload-file.model'
import { closeCaseManagementDialog } from '../../shared/utils/case-management-dialog-utils'

@Injectable({
    providedIn: 'root',
})
export class UploadService {
    private readonly upClients: Map<number, UploadTarget> = new Map()

    private currentUpTarget$: BehaviorSubject<UploadTarget | undefined> = new BehaviorSubject(
        undefined
    )

    private completedUploadsTrigger$ = new Subject<void>()

    datasetName$: Observable<string | undefined> = this.currentUpTarget$.pipe(
        map(upTarget => upTarget?.name)
    )

    currentUploads$: Observable<UpClientFile[] | undefined> = this.currentUpTarget$.pipe(
        switchMap(upTarget => {
            return upTarget?.currentUploads$ ?? of(undefined)
        }),
        map(uploads => uploads?.slice().reverse())
    )

    completedUploads$: Observable<UploadFileComponentModel[] | undefined> = combineLatest([
        this.currentUpTarget$,
        this.completedUploadsTrigger$.pipe(startWith(null)),
    ]).pipe(
        switchMap(([upTarget]) => {
            return upTarget === undefined
                ? of(undefined)
                : this.getCompletedUploadsForDataset(upTarget.client.datasetId)
        })
    )

    currentUploadProgress$: Observable<UploadProgress | undefined> = this.currentUpTarget$.pipe(
        switchMap(upTarget => upTarget?.currentTotalProgress$ ?? of(undefined))
    )

    uploadServiceIsReady$ = this.currentUpTarget$.pipe(map(upTarget => !!upTarget))

    private get currentUpClient(): UpClient | undefined {
        return this.currentUpTarget$.value?.client
    }

    private readonly updateCurrentUploadListFn = (up: UploadTarget): void => {
        up.currentUploads$.next(up.client.getCurrentUploads())
        this.completedUploadsTrigger$.next()
    }

    constructor(
        private readonly toasterService: ToasterService,
        private readonly uploadApi: UploadApiService
    ) {}

    setUploadId(uploadId: number, name: string): void {
        this.currentUpTarget$.next(undefined)

        if (this.upClients.has(uploadId)) {
            this.currentUpTarget$.next(this.upClients.get(uploadId))
        } else {
            new UpClientBuilder()
                .build(uploadId)
                .then((client: UpClient) => {
                    const uploader = {
                        client,
                        name,
                        currentUploads$: new BehaviorSubject<UpClientFile[] | undefined>(undefined),
                        currentTotalProgress$: new BehaviorSubject<UploadProgress | undefined>(
                            undefined
                        ),
                    }
                    client.addEventListener('ready', () => this.onReady(uploader))
                    client.addEventListener('removed', () =>
                        this.updateCurrentUploadListFn(uploader)
                    )
                    client.addEventListener('progress', () => this.onProgress(uploader))
                    client.addEventListener('complete', () => this.onComplete(uploader))
                    client.addEventListener('error', (message: string, file: UpClientFile) => {
                        this.onError(message, file, uploader)
                        client.cancel()
                        this.upClients.delete(uploadId)
                        closeCaseManagementDialog()
                    })

                    this.upClients.set(uploadId, uploader)
                    this.currentUpTarget$.next(uploader)
                })
                .catch((err: UpClientInitError) => {
                    this.toasterService.error(
                        'Uploading will not be possible at this time.',
                        'Error initializing upload'
                    )
                    console.error(err)
                })
        }
    }

    setBrowseElement(domNode: Node): void {
        this.currentUpClient?.setBrowseElement(domNode)
    }

    setDropElement(domNode: Node): void {
        this.currentUpClient?.setDropElement(domNode)
    }

    cancelFileUpload(id: string): void {
        this.currentUpClient?.cancelFile(id)
        if (this.currentUpTarget$.value) {
            this.updateCurrentUploadListFn(this.currentUpTarget$.value)
        }
    }

    submitDataset(
        isMultiUploadMode: boolean,
        createDatasetsPerFolder: boolean
    ): Observable<unknown> {
        if (!this.currentUpClient) return of(undefined)

        return this.uploadApi
            .submitDataset(
                this.currentUpClient.datasetId,
                isMultiUploadMode,
                createDatasetsPerFolder
            )
            .pipe(
                tap(() => {
                    this.toasterService.success(
                        `Dataset has been submitted for processing`,
                        'Dataset submitted'
                    )
                }),
                catchError((error: HttpErrorResponse) => {
                    this.toasterService.httpError(error, 'Error submitting dataset.')
                    return throwError(() => error)
                })
            )
    }

    private getCompletedUploadsForDataset(
        upClientId: number
    ): Observable<UploadFileComponentModel[]> {
        return this.uploadApi.getCompletedUploadsForDataset(upClientId).pipe(
            map(uploads =>
                uploads.map(upload => ({
                    ...upload,
                    previouslyCompleted: true,
                    size: upload.total_size,
                    name: upload.filename,
                    uniqueIdentifier: upload.identifier,
                }))
            )
        )
    }

    private onReady(up: UploadTarget): void {
        up.client.upload()
        this.updateCurrentUploadListFn(up)
        this.emitProgress(up)
    }

    private onProgress(up: UploadTarget): void {
        this.updateCurrentUploadListFn(up)
        this.emitProgress(up)
    }

    private emitProgress(up: UploadTarget): void {
        const progress = up.currentUploads$.value?.reduce(
            (
                { bytesUploaded, bytesTotal }: UploadProgress,
                file: UpClientFile
            ): UploadProgress => ({
                bytesUploaded: bytesUploaded + file.progress() * file.size,
                bytesTotal: bytesTotal + file.size,
            }),
            { bytesUploaded: 0, bytesTotal: 0 } as UploadProgress
        )
        up.currentTotalProgress$.next(
            progress
                ? { ...progress, ratio: progress.bytesUploaded / progress.bytesTotal }
                : undefined
        )
    }

    private onComplete(up: UploadTarget): void {
        this.updateCurrentUploadListFn(up)
    }

    private onError(message: string, file: UpClientFile, up: UploadTarget): void {
        console.log(message)
        this.toasterService.error(
            `File ${file.name} could not be uploaded. Please try again.`,
            'Error uploading file'
        )
        this.updateCurrentUploadListFn(up)
    }
}

export interface UploadProgress {
    bytesUploaded: number
    bytesTotal: number
    ratio?: number
}

interface UploadTarget {
    client: UpClient
    name: string
    currentUploads$: BehaviorSubject<UpClientFile[] | undefined>
    currentTotalProgress$: BehaviorSubject<UploadProgress | undefined>
}
